const MAP_CONSENT_KEY = 'htv:gmaps_consent';
const MAP_SCRIPT_SELECTOR = 'script[src*="maps.googleapis.com/maps/api/js"]';

const hasMapConsent = () => {
  try {
    return localStorage.getItem(MAP_CONSENT_KEY) === 'true'
      || sessionStorage.getItem(MAP_CONSENT_KEY) === 'true'
      || localStorage.getItem('htv_map_consent') === '1'
      || localStorage.getItem('htv_map_consent') === 'true';
  } catch {
    return false;
  }
};

const rememberMapConsent = () => {
  try {
    localStorage.setItem(MAP_CONSENT_KEY, 'true');
  } catch {
    return;
  }
};

const waitForExistingMapsScript = () => new Promise((resolve, reject) => {
  let attempts = 0;
  const timer = window.setInterval(() => {
    attempts += 1;

    if (window.google?.maps) {
      window.clearInterval(timer);
      resolve();
      return;
    }

    if (attempts >= 150) {
      window.clearInterval(timer);
      reject(new Error('Google Maps did not finish loading'));
    }
  }, 100);
});

const loadGoogleMaps = (apiKey) => {
  if (window.google?.maps) return Promise.resolve();
  if (window.__nlGoogleMapsPromise) return window.__nlGoogleMapsPromise;

  if (document.querySelector(MAP_SCRIPT_SELECTOR)) {
    window.__nlGoogleMapsPromise = waitForExistingMapsScript();
    return window.__nlGoogleMapsPromise;
  }

  window.__nlGoogleMapsPromise = new Promise((resolve, reject) => {
    const callbackName = `__nlCityMapsCallback_${Date.now()}`;
    window[callbackName] = () => {
      delete window[callbackName];
      resolve();
    };

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&callback=${callbackName}&v=weekly`;
    script.async = true;
    script.onerror = () => {
      delete window[callbackName];
      window.__nlGoogleMapsPromise = null;
      reject(new Error('Google Maps failed to load'));
    };
    document.head.appendChild(script);
  });

  return window.__nlGoogleMapsPromise;
};

class CityPharmacyMap extends HTMLElement {
  connectedCallback() {
    this.cards = [...this.querySelectorAll('[data-pharmacy-card]')];
    this.filters = [...this.querySelectorAll('[data-city-filter]')];
    this.citySections = [...this.querySelectorAll('[data-city-section]')];
    this.mapElement = this.querySelector('[data-city-map]');
    this.consent = this.querySelector('[data-map-consent]');
    this.consentButton = this.querySelector('[data-map-consent-button]');
    this.error = this.querySelector('[data-map-error]');

    this.filters.forEach((button) => {
      button.addEventListener('click', () => this.#filter(button.dataset.cityFilter));
    });
    this.cards.forEach((card) => {
      card.addEventListener('click', (event) => {
        if (event.target.closest('a, button')) return;
        this.#focusCard(card);
      });
    });
    this.consentButton?.addEventListener('click', () => {
      rememberMapConsent();
      this.#initializeMap();
    });

    const defaultCity = this.dataset.defaultCity || this.filters[0]?.dataset.cityFilter;
    if (defaultCity) this.#filter(defaultCity);

    if (hasMapConsent()) this.#initializeMap();
  }

  async #initializeMap() {
    if (this.map || !this.mapElement) return;

    const apiKey = window.__HTV_CONFIG__?.googleMapsApiKey;
    if (!apiKey) {
      this.#showError();
      return;
    }

    try {
      await loadGoogleMaps(apiKey);
      this.consent?.setAttribute('hidden', '');
      this.mapElement.removeAttribute('aria-hidden');

      this.map = new google.maps.Map(this.mapElement, {
        center: { lat: 51.1657, lng: 10.4515 },
        zoom: 6,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: true,
      });

      this.markers = this.cards.map((card) => {
        const position = {
          lat: Number(card.dataset.latitude),
          lng: Number(card.dataset.longitude),
        };
        if (!Number.isFinite(position.lat) || !Number.isFinite(position.lng)) return null;

        const marker = new google.maps.Marker({
          map: this.map,
          position,
          title: card.dataset.pharmacyName,
        });
        marker.addListener('click', () => {
          card.scrollIntoView({ behavior: 'smooth', block: 'center' });
          card.focus({ preventScroll: true });
        });

        return { card, marker, position };
      }).filter(Boolean);

      this.#fitVisibleMarkers();
    } catch {
      this.#showError();
    }
  }

  #filter(city) {
    this.filters.forEach((button) => {
      button.setAttribute('aria-pressed', String(button.dataset.cityFilter === city));
    });
    this.cards.forEach((card) => {
      card.hidden = city !== 'all' && card.dataset.city !== city;
    });
    this.citySections.forEach((section) => {
      section.hidden = city !== 'all' && section.dataset.city !== city;
    });
    this.#fitVisibleMarkers();
  }

  #fitVisibleMarkers() {
    if (!this.map || !this.markers) return;

    const bounds = new google.maps.LatLngBounds();
    let visibleCount = 0;
    for (const item of this.markers) {
      const visible = !item.card.hidden;
      item.marker.setMap(visible ? this.map : null);
      if (!visible) continue;
      bounds.extend(item.position);
      visibleCount += 1;
    }

    if (visibleCount > 0) this.map.fitBounds(bounds, 56);
  }

  #focusCard(card) {
    if (!this.map || !this.markers) return;
    const item = this.markers.find((entry) => entry.card === card);
    if (!item) return;
    this.map.panTo(item.position);
    this.map.setZoom(14);
  }

  #showError() {
    this.consent?.setAttribute('hidden', '');
    this.error?.removeAttribute('hidden');
  }
}

if (!customElements.get('city-pharmacy-map')) {
  customElements.define('city-pharmacy-map', CityPharmacyMap);
}
